import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { applyConsentSettings, parseConsentCookie } from './consent';
import {
  getStoredFeatureFlags,
  initAllFeatureFlags,
  initFeatureFlag,
  isFeatureFlagEnabled
} from './feature-flags';
import {
  getStoredUserId,
  identifyUser,
  refreshIdentityCookieStorage,
  resetIdentityOnLogout
} from './identity';
import { posthogLog } from './logger';
import {
  getStubbedEvents,
  initializeSDK,
  isPostHogEnabled,
  replayStubbedEvents,
  setupAPICredentials,
  stubPostHog
} from './sdk';
import type { FeatureFlags, FeatureFlagValue } from './types';

export const usePostHogStore = defineStore('posthog', () => {
  // State
  const initialized = ref(false);
  const hasConsent = ref(false);
  const flags = ref<FeatureFlags>({});
  const userId = ref<string | null>(null);

  // Promise that resolves when initialization is complete
  let resolveInitialization: (() => void) | null = null;
  const initializationComplete = new Promise<void>((resolve) => {
    resolveInitialization = resolve;
  });

  // Actions
  const initialize = async (): Promise<void> => {
    if (!isPostHogEnabled()) {
      resolveInitialization?.();
      posthogLog('PostHog disabled via Hobbii flags');
      return;
    }

    if (initialized.value) {
      posthogLog('PostHog already initialized');
      return;
    }

    const stubbedEvents = getStubbedEvents();
    refreshIdentityCookieStorage();

    const storedUserId = getStoredUserId();
    userId.value = storedUserId;

    initializeSDK();
    posthogLog('PostHog SDK initialized');

    if (storedUserId) {
      identifyUser(storedUserId);
      posthogLog('User identified:', storedUserId);
    }

    resetIdentityOnLogout();

    hasConsent.value = parseConsentCookie();
    applyConsentSettings(hasConsent.value);
    posthogLog('Consent status:', hasConsent.value);

    if (hasConsent.value) {
      await initAllFeatureFlags();
      flags.value = getStoredFeatureFlags();
      posthogLog('Feature flags initialized:', flags.value);

      replayStubbedEvents(stubbedEvents);
      posthogLog('Replayed', stubbedEvents.length, 'stubbed events');
    }

    initialized.value = true;
    resolveInitialization?.();
    posthogLog('PostHog initialization complete');
  };

  const waitForInitialization = (): Promise<void> => {
    if (initialized.value) {
      return Promise.resolve();
    }
    return initializationComplete;
  };

  const initStub = (): void => {
    stubPostHog();
    posthogLog(
      'PostHog stubbed - events will be replayed after initialization'
    );
  };

  const cacheFeatureFlag = async (
    flagName: string
  ): Promise<FeatureFlagValue> => {
    const existingFlag = getFeatureFlag(flagName);
    if (existingFlag !== undefined) {
      return existingFlag;
    }

    await waitForInitialization();

    if (!hasConsent.value) {
      return undefined;
    }

    const flagValue = initFeatureFlag(flagName);
    flags.value = getStoredFeatureFlags();
    return flagValue;
  };

  const getFeatureFlag = (flagName: string): FeatureFlagValue | undefined => {
    return flags.value[flagName];
  };

  const isFeatureEnabled = (flagName: string): boolean => {
    return isFeatureFlagEnabled(flags.value[flagName]);
  };

  const updateConsent = async (consent: boolean): Promise<void> => {
    hasConsent.value = consent;
    applyConsentSettings(consent);
    posthogLog('Consent updated:', consent);

    if (consent && Object.keys(flags.value).length === 0) {
      await waitForInitialization();
      await initAllFeatureFlags();
      flags.value = getStoredFeatureFlags();
    }
  };

  return {
    // Getters
    isInitialized: computed(() => initialized.value),
    consentGiven: computed(() => hasConsent.value),
    featureFlags: computed(() => flags.value),
    distinctId: computed(() => userId.value),
    isEnabled: computed(() => isPostHogEnabled()),
    // Actions
    setupAPICredentials,
    stubPostHog: initStub,
    initialize,
    waitForInitialization,
    cacheFeatureFlag,
    getFeatureFlag,
    isFeatureEnabled,
    updateConsent,
    debugLog: posthogLog
  };
});
